import { Component, OnDestroy, OnInit } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TableLazyLoadEvent } from 'primeng/table';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { State } from 'src/app/core/store';
import * as actions from 'src/app/core/store/credential-transitions/credential-transitions.actions';
import { loadEmployeeUsers } from 'src/app/core/store/employee/employee.action';
import { EmployeeTransitionDetail, EmployeeTransitionFilter, EmployeeTransitionVersion } from 'src/app/models/credentialTransitionManagement';
import { UserAutoCompleteItem } from 'src/app/models/utilModels';

@Component({
  selector: 'app-employee-transitions',
  templateUrl: './employee-transitions.component.html',
  standalone: false
})
export class EmployeeTransitionsComponent extends BaseComponent implements OnInit, OnDestroy
{
  state$ = this.store.select(state => state.credentialTransitionsState);
  filter: EmployeeTransitionFilter =
  {
    userID: null,
    includeInactive: false,
    campusID: null,
    processID: null,
    page: 0,
    pageSize: 20,
    sortField: 'createdOn',
    sortOrder: -1
  };
  selectedUserEmployee: UserAutoCompleteItem;
  userEmployeeList: UserAutoCompleteItem[] = [];
  includeInactiveOptions =
  [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];
  detail: EmployeeTransitionDetail;
  examDate = '';
  versionID: number;

  constructor(private store: Store<State>, private actions$: Actions)
  {
    super();
  }

  ngOnInit()
  {
    this.store.dispatch(actions.clearEmployeeTransitions());
    this.subs.push(
      this.store.select(state => state.employeeState.employeeUsers).subscribe(users =>
      {
        this.userEmployeeList = (users || []).map(user => ({ id: user.value, name: user.label }));
      }),
      this.store.select(state => state.credentialTransitionsState.detail).subscribe(detail =>
      {
        this.detail = detail;
        this.examDate = detail?.status.examDate?.substring(0, 10) || '';
        this.versionID = detail?.processVersionID;
      }),
      this.actions$.pipe(ofType(actions.employeeTransitionSaved)).subscribe(() => this.load(false))
    );
    this.store.dispatch(actions.loadEmployeeTransitionLookups());
  }

  ngOnDestroy()
  {
    super.ngOnDestroy();
    this.store.dispatch(actions.clearEmployeeTransitions());
  }

  load(resetPage = true)
  {
    if (resetPage)
      this.filter.page = 0;

    this.store.dispatch(actions.loadEmployeeTransitions({ filter: { ...this.filter } }));
  }

  reloadFilter(event: TableLazyLoadEvent)
  {
    this.filter.pageSize = event.rows || 20;
    this.filter.page = Math.floor((event.first || 0) / this.filter.pageSize);
    this.filter.sortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField || 'createdOn';
    this.filter.sortOrder = event.sortOrder === 1 ? 1 : -1;
    this.load(false);
  }

  getStaffList(event: { query: string })
  {
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      programId: this.filter.campusID,
      payrollClassificationId: null,
      showInactive: this.filter.includeInactive,
      showTerminated: this.filter.includeInactive
    }));
  }

  employeeChanged(employee: UserAutoCompleteItem = null)
  {
    this.selectedUserEmployee = employee;
    this.filter.userID = employee?.id || null;
    this.load();
  }

  open(id: number)
  {
    this.store.dispatch(actions.loadEmployeeTransition({ id }));
  }

  close()
  {
    this.store.dispatch(actions.closeEmployeeTransition());
  }

  get selectedVersion(): EmployeeTransitionVersion
  {
    return this.detail?.versions.find(version => version.id === this.versionID);
  }

  get hasUnsavedChanges(): boolean
  {
    return !!this.detail && (this.versionID !== this.detail.processVersionID
      || this.examDate !== (this.detail.status.examDate?.substring(0, 10) || ''));
  }

  save()
  {
    if (!this.detail?.canManage || !this.hasUnsavedChanges)
      return;
    if (this.versionID !== this.detail.processVersionID
      && !window.confirm('Apply this version to the transition? Its requirements will change. Changing the target credential requires it to be verified again.'))
      return;

    this.store.dispatch(actions.updateEmployeeTransition({ id: this.detail.id, model: {
      processVersionID: this.versionID,
      expectedProcessVersionID: this.detail.processVersionID,
      examDate: this.examDate || null,
      expectedExamDate: this.detail.status.examDate?.substring(0, 10) || null
    } }));
  }

  advance()
  {
    if (!this.detail?.canManage || this.hasUnsavedChanges)
      return;

    this.store.dispatch(actions.advanceEmployeeTransition({ id: this.detail.id, model: {
      processVersionID: this.detail.processVersionID,
      attemptID: this.detail.status.attemptID,
      confirmPayDecrease: false
    } }));
  }
}
